// src/services/api.jsx

// --- Environment Configuration ---
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.simpleclass.eu';
const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL || 'https://adminapi.simpleclass.eu';

const USE_SUBDOMAIN_ROUTING = import.meta.env.VITE_USE_SUBDOMAIN_ROUTING === 'true';

// --- API Service Paths ---
const AUTH_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const USER_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const COURSE_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const SESSION_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const CHAT_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const CALENDAR_SERVICE_BASE_PATH = `${API_BASE_URL}`;
const INSTITUTION_SERVICE_BASE_PATH = `${API_BASE_URL}`;

// --- Token Management ---
export function getToken() {
  return localStorage.getItem('authToken');
}

export function setToken(token) {
  localStorage.setItem('authToken', token);
}

export function removeToken() {
  localStorage.removeItem('authToken');
}

// --- User Data Management ---
export function getUserData() {
  const userData = localStorage.getItem('userData');
  return userData ? JSON.parse(userData) : null;
}

export function setUserData(data) {
  localStorage.setItem('userData', JSON.stringify(data));
}

export function removeUserData() {
  localStorage.removeItem('userData');
}

// --- Institution Context Management ---
export function getCurrentInstitutionId() {
  return localStorage.getItem('currentInstitutionId');
}

export function getCurrentInstitutionSubdomain() {
  const institution = localStorage.getItem('selectedInstitution');
  if (institution) {
    try {
      const parsed = JSON.parse(institution);
      return parsed.subdomain;
    } catch (e) {
      return null;
    }
  }
  return null;
}

// --- Logout Function ---
export async function logoutUser() {
  removeToken();
  removeUserData();
  localStorage.removeItem('rememberedEmail');
  localStorage.removeItem('currentInstitutionId');
  localStorage.removeItem('selectedInstitution');
  // Remove all institution-specific roles
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('userRole_')) {
      localStorage.removeItem(key);
    }
  });
  return { success: true };
}

// --- Generic API Request Helper ---
async function apiRequest(url, method = 'GET', body = null, requiresAuth = true) {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (requiresAuth) {
    const token = getToken();
    if (!token) {
      console.error('No auth token available for authenticated request');
      const error = new Error('Authentication required');
      error.statusCode = 401;
      error.data = { success: false, message: 'Authentication required' };
      throw error;
    }
    headers['Authorization'] = `Bearer ${token}`;
    
    // Add institution context headers
    const institutionId = getCurrentInstitutionId();
    const institutionSubdomain = getCurrentInstitutionSubdomain();
    
    if (institutionId) {
      headers['X-Institution-Id'] = institutionId;
    }
    if (institutionSubdomain) {
      headers['X-Institution-Subdomain'] = institutionSubdomain;
    }
  }

  const options = {
    method,
    headers,
  };

  // FIXED: Include DELETE in the condition
  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
    options.body = JSON.stringify(body);
    // Add logging for debugging
    console.log(`=== API Request to ${url} ===`);
    console.log('Method:', method);
    console.log('Body:', body);
    console.log('Headers:', headers);
  }

  try {
    const response = await fetch(url, options);
    
    let responseData;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      const textResponse = await response.text();
      if (!response.ok) {
        const error = new Error(`HTTP error! Status: ${response.status}`);
        error.statusCode = response.status;
        error.data = { success: false, message: textResponse || `Request failed with status ${response.status}`};
        throw error;
      }
      responseData = { success: true, message: textResponse, data: textResponse, status: response.status };
    }

    // Handle authentication errors (401 only)
    if (response.status === 401) {
      console.error('Authentication error detected:', response.status);
      
      // Check if this is a login attempt
      const isLoginEndpoint = url.includes('/auth/login') || 
                             url.includes('/auth/admin-login') || 
                             url.includes('/auth/complete-invitation');
      
      // Only call global auth error handler for token failures, not login attempts
      if (!isLoginEndpoint && window.handleAuthError) {
        console.log('Calling handleAuthError for token expiration');
        setTimeout(() => window.handleAuthError(true), 100);
      }
      
      const error = new Error(responseData?.message || 'Authentication failed');
      error.statusCode = response.status;
      error.data = responseData || { success: false, message: 'Authentication failed' };
      throw error;
    }
    
    // Handle authorization errors (403) - DO NOT log out the user!
    if (response.status === 403) {
      console.warn('Authorization error detected:', response.status, responseData?.message);
      
      const error = new Error(responseData?.message || 'Access forbidden');
      error.statusCode = response.status;
      error.data = responseData || { success: false, message: 'Access forbidden' };
      throw error;
    }

    if (!response.ok) {
      const error = new Error(responseData?.message || `HTTP error! Status: ${response.status}`);
      error.statusCode = response.status;
      error.data = responseData || { success: false, message: `Request failed with status ${response.status}` };
      throw error;
    }
    
    return responseData.success !== undefined ? responseData : { success: true, ...responseData };
  } catch (error) {
    console.error(`API Request Helper Error for ${method} ${url}:`, error.message, error.statusCode, error.data || error);
    if (!error.data && !error.statusCode) {
      const genericError = new Error('Network error or an unexpected issue occurred while contacting the server.');
      genericError.statusCode = error.statusCode || 503;
      genericError.data = { success: false, message: error.message || 'Network error or an unexpected issue occurred.'};
      throw genericError;
    }
    throw error;
  }
}

// --- Auth API Functions ---
export async function loginUser(email, password, institutionId) {
  console.log('=== loginUser API function called ===');
  console.log('Parameters received:', { email, password: '***', institutionId });
  
  if (!institutionId) {
    console.error('ERROR: institutionId is missing!');
    throw new Error('Institution ID is required for login');
  }
  
  const requestBody = { 
    email, 
    password, 
    institutionId: parseInt(institutionId, 10) 
  };
  
  console.log('Request body being sent:', { ...requestBody, password: '***' });
  
  const response = await apiRequest(
    `${AUTH_SERVICE_BASE_PATH}/auth/login`, 
    'POST', 
    requestBody, 
    false
  );
  
  console.log('Login API response:', response);
  
  if (response.success && response.data?.token) {
    setToken(response.data.token);
    
    // Store institution-specific data
    localStorage.setItem('currentInstitutionId', institutionId.toString());
    
    // Store user data with institution context (UPDATED: fullName -> displayName)
    setUserData({
      userId: response.data.userId,
      email: response.data.email,
      displayName: response.data.displayName,
      emailVerified: response.data.emailVerified,
      userRole: response.data.userRole,
      institutionId: institutionId,
      institutionMemberId: response.data.institutionMemberId
    });
    
    // Store role for this specific institution
    if (response.data.userRole) {
      localStorage.setItem(`userRole_${institutionId}`, response.data.userRole);
    }
  }
  return response;
}

// --- User Profile API Functions ---
export async function getProfile() {
  const response = await apiRequest(`${USER_SERVICE_BASE_PATH}/users/profile`, 'GET');
  console.log('Profile API raw response:', response);
  
  // Normalize the response structure
  if (response.success) {
    // Handle different possible response structures from the backend
    let normalizedData = {
      user: null,
      institutions: []
    };
    
    // If data is directly the user object
    if (response.data && !response.data.user && !response.data.institutions) {
      normalizedData.user = response.data;
      normalizedData.institutions = response.data.institutions || [];
    }
    // If data has user and institutions separately
    else if (response.data) {
      normalizedData.user = response.data.user || response.data;
      normalizedData.institutions = response.data.institutions || [];
    }
    
    return {
      success: true,
      data: normalizedData
    };
  }
  
  return response;
}

export async function updateProfile(profileData) {
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/profile`, 'PUT', profileData);
}

export async function deleteAccount(password) {
  // This will completely delete the user account from all institutions
  const response = await apiRequest(`${USER_SERVICE_BASE_PATH}/users/profile`, 'DELETE', { 
   currentPassword: password
  });
  // If successful, clear all local data
  if (response.success) {
    await logoutUser();
  }
  
  return response;
}

// --- Language API Functions --- (Add after User Profile functions)
export async function updateLanguage(language) {
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/language`, 'PUT', { language });
}

// --- Multi-Institution User Functions ---
export async function getUserInstitutions() {
  // Get all institutions the current user belongs to
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/institutions`, 'GET');
}

export async function switchInstitution(institutionId) {
  // Switch the active institution context
  localStorage.setItem('currentInstitutionId', institutionId.toString());
  
  // Optionally notify the backend about the switch
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/switch-institution`, 'POST', { institutionId });
}

export async function getUserRoleInInstitution(institutionId) {
  // Get the user's role in a specific institution
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/institutions/${institutionId}/role`, 'GET');
}

// --- Course API Functions ---
export async function getCourses() {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses`, 'GET');
}

export async function getCourse(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'GET');
}

export async function createCourse(courseData) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses`, 'POST', courseData);
}

export async function updateCourse(courseId, courseData) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'PUT', courseData);
}

export async function deleteCourse(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'DELETE');
}

export async function getCourseParticipants(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/participants`, 'GET');
}

export async function getCourseStudents(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/students`, 'GET');
}

export async function addCourseParticipant(courseId, participantData) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/participants`, 'POST', participantData);
}

export async function removeCourseParticipant(courseId, participantId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/participants/${participantId}`, 'DELETE');
}

// --- Course API Functions (Institution-specific) ---
export async function getCoursesByInstitution(institutionId) {
  // Fixed: Use the correct endpoint structure from the backend
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/by-institution/${institutionId}`, 'GET');
}

export async function getCourseTeachers(courseId) {
  // Get all teachers assigned to a course
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/teachers`, 'GET');
}

// --- Session Chat API Functions ---
export async function getSessionChatHistory(sessionId, options = {}) {
  const { limit = 50, offset = 0 } = options;
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/chat/messages?${params.toString()}`, 'GET');
}

export async function sendSessionChatMessage(sessionId, messageData) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/chat/messages`, 'POST', messageData);
}

// --- MyCourses Component API Functions ---
export async function getMyCourses() {
  // This should return courses for the current user in the current institution
  const institutionId = getCurrentInstitutionId();
  if (!institutionId) {
    return { success: false, message: 'No institution selected' };
  }
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/by-institution/${institutionId}`, 'GET');
}

export async function getMyStudents() {
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/my-students`, 'GET');
}

export async function getStudentDetail(institutionMemberId) {
  return apiRequest(`${USER_SERVICE_BASE_PATH}/users/my-students/${institutionMemberId}`, 'GET');
}

export async function getCourseSessions(courseId) {
  // FIXED: Use the correct endpoint that matches your backend routes
    return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions?courseId=${courseId}`, 'GET');
}

export async function createSession(courseId, sessionData) {
  // Send courseId in the body, not in the URL
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions`, 'POST', {
    courseId: parseInt(courseId, 10),
    ...sessionData
  });
}

export async function deleteSession(sessionId) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'DELETE');
}

export async function enrollStudentsInCourse(courseId, studentInstitutionMemberId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/students`, 'POST', {
    institutionMemberId: studentInstitutionMemberId  // <-- CORRECT
  });
}

export async function unenrollStudentsFromCourse(courseId, studentInstitutionMemberId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/students/${studentInstitutionMemberId}`, 'DELETE');
}

export async function getAvailableStudentsForCourse(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/available-students`, 'GET');
}

export async function getAvailableTeachersForCourse(courseId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/available-teachers`, 'GET');
}

export async function addCourseTeacher(courseId, teacherData) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/teachers`, 'POST', teacherData);
}

export async function removeCourseTeacher(courseId, teacherInstitutionMemberId) {
  return apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/teachers/${teacherInstitutionMemberId}`, 'DELETE');
}

// --- Session API Functions ---
export async function getSessions(courseId = null) {
  const queryParam = courseId ? `?courseId=${courseId}` : '';
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions${queryParam}`, 'GET');
}

export async function updateSession(sessionId, sessionData) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'PUT', sessionData);
}

export async function getSessionDetails(sessionId) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'GET');
}

export async function markAttendance(sessionId, attendanceData) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/attendance`, 'POST', attendanceData);
}

export async function updateAttendance(sessionId, attendanceId, attendanceData) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/attendance/${attendanceId}`, 'PUT', attendanceData);
}

export async function getSessionRecordings(sessionId) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/recordings`, 'GET');
}

// --- Calendar API Functions ---
export async function getCalendarSessions(startDate, endDate) {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  return apiRequest(`${CALENDAR_SERVICE_BASE_PATH}/calendar/sessions?${params.toString()}`, 'GET');
}

// --- Chat API Functions ---
export async function getChatContacts() {
  return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/contacts`, 'GET');
}

export async function getChatMessages(otherUserId, limit = 50, beforeMessageId = null) {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (beforeMessageId) params.append('beforeMessageId', beforeMessageId);
  
  return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/users/${otherUserId}/messages?${params.toString()}`, 'GET');
}

// Update this function in api.jsx
export async function sendChatMessage(receiverUserId, messageData) {
  // Support both old string format and new object format
  if (typeof messageData === 'string') {
    // Legacy support
    return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/users/${receiverUserId}/messages`, 'POST', { 
      messageContent: messageData 
    });
  }
  
  // New format with attachment support
  const payload = {
    messageContent: messageData.message || messageData.messageContent || '',
    attachmentName: messageData.attachmentName,
    attachmentType: messageData.attachmentType,
    attachmentSizeKb: messageData.attachmentSizeKb,
    attachmentUrl: messageData.attachmentUrl
  };
  
  return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/users/${receiverUserId}/messages`, 'POST', payload);
}

// Add these new functions for file handling
// In api.jsx, update the uploadChatAttachment function:
export async function uploadChatAttachment(formData, onProgress) {
  const token = getToken();
  if (!token) {
    const error = new Error('Authentication required');
    error.statusCode = 401;
    throw error;
  }

  const institutionId = getCurrentInstitutionId();
  const institutionSubdomain = getCurrentInstitutionSubdomain();

  try {
    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        console.log('Upload response status:', xhr.status);
        console.log('Upload response:', xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid response format'));
          }
        } else {
          let errorMessage = `Upload failed with status: ${xhr.status}`;
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            errorMessage = errorResponse.message || errorMessage;
          } catch (e) {
            errorMessage = xhr.responseText || errorMessage;
          }
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('POST', `${CHAT_SERVICE_BASE_PATH}/chat/attachments/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      if (institutionId) {
        xhr.setRequestHeader('X-Institution-Id', institutionId);
      }
      if (institutionSubdomain) {
        xhr.setRequestHeader('X-Institution-Subdomain', institutionSubdomain);
      }

      xhr.send(formData);
    });
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
}

// Add this new function to api.jsx for session chat attachments
export async function uploadSessionChatAttachment(sessionId, formData, onProgress) {
  const token = getToken();
  if (!token) {
    const error = new Error('Authentication required');
    error.statusCode = 401;
    throw error;
  }

  const institutionId = getCurrentInstitutionId();
  const institutionSubdomain = getCurrentInstitutionSubdomain();

  try {
    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        console.log('Session upload response status:', xhr.status);
        console.log('Session upload response:', xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid response format'));
          }
        } else {
          let errorMessage = `Upload failed with status: ${xhr.status}`;
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            errorMessage = errorResponse.message || errorMessage;
          } catch (e) {
            errorMessage = xhr.responseText || errorMessage;
          }
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      // For now, we'll use the personal chat upload endpoint but modify the data
      // In production, you should create a dedicated session attachment endpoint
      xhr.open('POST', `${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/attachments/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      if (institutionId) {
        xhr.setRequestHeader('X-Institution-Id', institutionId);
      }
      if (institutionSubdomain) {
        xhr.setRequestHeader('X-Institution-Subdomain', institutionSubdomain);
      }

      xhr.send(formData);
    });
  } catch (error) {
    console.error('Session file upload error:', error);
    throw error;
  }
}

export async function deleteChatAttachment(attachmentUrl) {
  return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/attachments`, 'DELETE', { attachmentUrl });
}

export async function markMessagesAsRead(otherUserId) {
  return apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/conversations/${otherUserId}/mark-read`, 'POST');
}

// --- Institution API Functions ---
export async function getInstitutionBySubdomain(subdomain) {
  return apiRequest(`${INSTITUTION_SERVICE_BASE_PATH}/institutions/by-subdomain/${subdomain}`, 'GET', null, false);
}

export async function fetchUserProfileAPI() {
  return getProfile();
}

// --- Auth Service API object (for backwards compatibility) ---
export const authAPI = {
  initiateRegistration: (institutionName, subdomain, email, password) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/initiate-registration`, 'POST', { institutionName, subdomain, email, password }, false),
  completeFullRegistration: (registrationSessionToken, businessDetails) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/complete-full-registration`, 'POST', { registrationSessionToken, businessDetails }, false),
  adminLogin: (email, password) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/admin-login`, 'POST', { email, password }, false),
  userLogin: (email, password, institutionId) => {
    console.log('authAPI.userLogin called with institutionId:', institutionId);
    return loginUser(email, password, institutionId);
  },
  forgotPassword: (email, accountType) =>
    apiRequest(`${ADMIN_API_URL}/api/v1/auth/forgot-password`, 'POST', { email, accountType }, false),
  resetPassword: (token, newPassword, accountType) =>
    apiRequest(`${ADMIN_API_URL}/api/v1/auth/reset-password`, 'POST', { token, newPassword, accountType }, false),
  verifyEmail: (token, type) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/verify-email?token=${token}&type=${type}`, 'GET', null, false),
  completeInvitationSetup: (token, password) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/complete-invitation`, 'POST', { token, password }, false),
  resendSetupEmail: (emailPayload) =>
    apiRequest(`${AUTH_SERVICE_BASE_PATH}/auth/internal/send-password-setup-email`, 'POST', emailPayload, true),
};

// --- User Profile API object ---
export const userAPI = {
  getProfile: () => getProfile(),
  updateProfile: (data) => apiRequest(`${USER_SERVICE_BASE_PATH}/users/profile`, 'PUT', data),
  changePassword: (currentPassword, newPassword) => 
    apiRequest(`${USER_SERVICE_BASE_PATH}/users/change-password`, 'POST', { currentPassword, newPassword }),
  deleteAccount: (password) => deleteAccount(password),
  getNotificationPreferences: () => apiRequest(`${USER_SERVICE_BASE_PATH}/users/notification-preferences`, 'GET'),
  updateNotificationPreferences: (preferences) => 
    apiRequest(`${USER_SERVICE_BASE_PATH}/users/notification-preferences`, 'PUT', preferences),
};

// --- Course API object ---
export const courseAPI = {
  getCourses: () => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses`, 'GET'),
  getCourse: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'GET'),
  createCourse: (courseData) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses`, 'POST', courseData),
  updateCourse: (courseId, courseData) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'PUT', courseData),
  deleteCourse: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}`, 'DELETE'),
  enrollInCourse: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/enroll`, 'POST'),
  unenrollFromCourse: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/unenroll`, 'POST'),
  getCourseStudents: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/students`, 'GET'),
  getCourseContent: (courseId) => apiRequest(`${COURSE_SERVICE_BASE_PATH}/courses/${courseId}/content`, 'GET'),
  uploadCourseContent: (courseId, formData) => {
    const token = getToken();
    return fetch(`${API_BASE_URL}/courses/${courseId}/content`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }).then(res => res.json());
  },
};

// --- Session API object ---
export const sessionAPI = {
  getUpcomingSessions: () => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/upcoming`, 'GET'),
  getSessionsByCourse: (courseId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions?courseId=${courseId}`, 'GET'),
  getSession: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'GET'),
  createSession: (sessionData) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions`, 'POST', sessionData),
  updateSession: (sessionId, sessionData) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'PUT', sessionData),
  deleteSession: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}`, 'DELETE'),
  joinSession: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/join`, 'POST'),
  endSession: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/end`, 'POST'),
  getSessionRecording: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/recording`, 'GET'),
  getMediaRestrictions: (sessionId) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/media-restrictions`, 'GET'),
  updateMediaRestrictions: (sessionId, restrictions) => apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/media-restrictions`, 'POST', restrictions),
};

// --- Chat API object ---
export const chatAPI = {
  getContacts: () => apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/contacts`, 'GET'),
  getMessages: (contactId, page = 1, limit = 50) => 
    apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/messages/${contactId}?page=${page}&limit=${limit}`, 'GET'),
  sendMessage: (contactId, message) => 
    apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/messages`, 'POST', { receiverId: contactId, message }),
  markMessagesAsRead: (contactId) => 
    apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/messages/${contactId}/read`, 'PUT'),
  getUnreadCount: () => apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/unread-count`, 'GET'),
};

// --- Calendar API object ---
export const calendarAPI = {
  getCalendarSessions: (startDate, endDate) => 
    apiRequest(`${CALENDAR_SERVICE_BASE_PATH}/calendar/sessions?startDate=${startDate}&endDate=${endDate}`, 'GET'),
  getUpcomingEvents: (limit = 10) => 
    apiRequest(`${CALENDAR_SERVICE_BASE_PATH}/calendar/upcoming?limit=${limit}`, 'GET'),
};

// --- Institution API object ---
export const institutionAPI = {
  getInstitutionBySubdomain: (subdomain) => 
    apiRequest(`${INSTITUTION_SERVICE_BASE_PATH}/institutions/by-subdomain/${subdomain}`, 'GET', null, false),
  getInstitutionDetails: (institutionId) => 
    apiRequest(`${INSTITUTION_SERVICE_BASE_PATH}/institutions/${institutionId}`, 'GET'),
  updateInstitutionSettings: (institutionId, settings) => 
    apiRequest(`${INSTITUTION_SERVICE_BASE_PATH}/institutions/${institutionId}/settings`, 'PUT', settings),
};

// --- Helper function to extract subdomain ---
function getSubdomain() {
  if (!USE_SUBDOMAIN_ROUTING) return null;
  
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  if (hostname === 'localhost' || hostname.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
    return null;
  }
  
  if (parts.length >= 3) {
    return parts[0];
  }
  
  return null;
}

// --- Additional Helper Functions ---
export function isAuthenticated() {
  return !!getToken();
}

export function getCurrentUser() {
  return getUserData();
}

export function clearAllUserData() {
  logoutUser();
}

// --- WebSocket Configuration (if needed) ---
export function getWebSocketUrl() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = import.meta.env.VITE_WS_URL || window.location.host;
  return `${protocol}//${host}`;
}

// Add these functions to api.jsx

export async function getOneDriveMetadata(url) {
  try {
    const response = await apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/onedrive-metadata?url=${encodeURIComponent(url)}`, 'GET');
    return response;
  } catch (error) {
    console.error('Failed to fetch OneDrive metadata:', error);
    // Check if it's a 400 error
    if (error.statusCode === 400) {
      console.error('URL validation failed. URL:', url);
      console.error('Encoded URL:', encodeURIComponent(url));
    }
    return {
      success: false,
      data: {
        filename: 'OneDrive Document'
      }
    };
  }
}

export async function getGoogleDriveMetadata(url) {
  try {
    const response = await apiRequest(`${CHAT_SERVICE_BASE_PATH}/chat/google-drive-metadata?url=${encodeURIComponent(url)}`, 'GET');
    return response;
  } catch (error) {
    console.error('Failed to fetch Google Drive metadata:', error);
    return {
      success: false,
      data: {
        filename: 'Google Document'
      }
    };
  }
}

// Update the completeInvitationSetup function (around line 1064)
export const completeInvitationSetup = async (token, password, confirmPassword) => {
  const response = await fetch(`${API_BASE_URL}/auth/complete-invitation`, {  // Remove '/portal' 
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password, confirmPassword })
  });
  return response.json();
};

// Also update checkPasswordStrength (around line 1056)
export const checkPasswordStrength = async (password) => {
  const response = await fetch(`${API_BASE_URL}/auth/check-password-strength`, {  // Remove '/portal'
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  });
  return response.json();
};

// --- Media Restrictions API Functions ---
export async function getMediaRestrictions(sessionId) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/media-restrictions`, 'GET');
}

export async function updateMediaRestrictions(sessionId, restrictions) {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/${sessionId}/media-restrictions`, 'POST', restrictions);
}

// Add this to your api.jsx
export async function getUpcomingSessions() {
  return apiRequest(`${SESSION_SERVICE_BASE_PATH}/sessions/upcoming`, 'GET');
}

// --- Error Response Handler ---
export function handleApiError(error) {
  if (error.statusCode === 401) {
    // Unauthorized - redirect to login
    logoutUser();
    window.location.href = '/login';
  } else if (error.statusCode === 403) {
    // Forbidden - show permission error
    return { 
      success: false, 
      message: 'You do not have permission to perform this action' 
    };
  } else if (error.statusCode >= 500) {
    // Server error
    return { 
      success: false, 
      message: 'A server error occurred. Please try again later.' 
    };
  }
  
  // Return the original error
  return error.data || { 
    success: false, 
    message: error.message || 'An unexpected error occurred' 
  };
}